import { useMemo, useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type {
  CsApplicability,
  CsAudit,
  CsAuditFrequency,
  CsAuditType,
  CsCatalogAudit,
  CsClient,
  CsTierKey,
  Employee,
} from '../../lib/types';

/**
 * Audits — internal and client, each with a checklist that rolls up into its
 * card, and a cadence rather than a status (see migration 040).
 *
 * The Catalog tab is the substantial part: the ten standard MSP audits at
 * three maturity tiers each, any of which can be started as a tracked audit.
 * Since migration 041 those live in the database, so they can be added to and
 * corrected here rather than in a deploy. That also replaced audit templates,
 * which were the same idea with less in them.
 */

type Tab = 'all' | 'internal' | 'client' | 'catalog';

/** Short on the card, where four sit in a row; long in the editor. */
const FREQUENCY_LABELS: Record<CsAuditFrequency, { short: string; long: string }> = {
  monthly: { short: 'M', long: 'Monthly' },
  quarterly: { short: 'Q', long: 'Quarterly' },
  biannual: { short: '6M', long: 'Bi-annually' },
  annual: { short: 'Y', long: 'Annually' },
};

const FREQUENCY_ORDER: CsAuditFrequency[] = ['monthly', 'quarterly', 'biannual', 'annual'];

const TIERS: { key: CsTierKey; label: string }[] = [
  { key: 'foundational', label: 'Foundational' },
  { key: 'standard', label: 'Standard' },
  { key: 'advanced', label: 'Advanced' },
];

const APPLICABILITY: { key: CsApplicability; label: string }[] = [
  { key: 'internal', label: 'Internal only' },
  { key: 'client', label: 'Client only' },
  { key: 'both', label: 'Both' },
];

export function CsAudits({
  onChanged,
  focusClientId = null,
}: {
  onChanged: () => void;
  /**
   * Open straight onto one client's audits.
   *
   * Set when arriving from the Audit Matrix button in account management, which
   * links here rather than showing a second copy of the matrix — the audits are
   * CS's, and this is where they are worked. Only an INITIAL value: the tab and
   * the picker are ordinary state afterwards, so following the link does not
   * trap the reader on one client.
   */
  focusClientId?: number | null;
}) {
  const audits = useResource<CsAudit[]>('/cs/audits');
  const clients = useResource<CsClient[]>('/cs/clients');
  const employees = useResource<Employee[]>('/employees');
  const [tab, setTab] = useState<Tab>(focusClientId === null ? 'all' : 'client');
  /* Which client the Client tab is narrowed to. Null is "all of them". */
  const [clientId, setClientId] = useState<number | null>(focusClientId);
  const [editing, setEditing] = useState<{ audit: CsAudit | null; seed?: SeedAudit } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    audits.reload();
    onChanged();
  };

  const counts = useMemo(() => {
    const list = audits.data ?? [];
    return {
      all: list.length,
      internal: list.filter((a) => a.AuditType === 'internal').length,
      client: list.filter((a) => a.AuditType === 'client').length,
    };
  }, [audits.data]);

  const visible = (audits.data ?? []).filter((a) => {
    if (tab === 'internal') return a.AuditType === 'internal';
    if (tab === 'client') {
      if (a.AuditType !== 'client') return false;
      return clientId === null || a.ClientId === clientId;
    }
    return true;
  });

  /* Only companies that actually have an audit — a picker listing all 188
     clients would be a scroll through mostly-empty choices. */
  const clientsWithAudits = useMemo(() => {
    const ids = new Set(
      (audits.data ?? []).filter((a) => a.AuditType === 'client' && a.ClientId).map((a) => a.ClientId),
    );
    return (clients.data ?? []).filter((c) => ids.has(c.ClientId));
  }, [audits.data, clients.data]);

  const TABS: { key: Tab; label: string; count?: number }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'internal', label: 'Internal', count: counts.internal },
    { key: 'client', label: 'Client', count: counts.client },
    { key: 'catalog', label: 'Catalog' },
  ];

  return (
    <>
      <div className="row-between wrap mb-4">
        <div className="tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`tab${tab === t.key ? ' active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.count !== undefined && t.count > 0 && <span className="xs muted"> {t.count}</span>}
            </button>
          ))}
        </div>
        {tab !== 'catalog' && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({ audit: null })}>
            New audit
          </button>
        )}
      </div>

      {error && <ErrorBanner message={error} />}
      {audits.error && <ErrorBanner message={audits.error} />}

      {tab === 'client' &&
        (clientsWithAudits.length === 0 ? (
          /* A picker with nothing in it reads as broken. It happens when client
             audits exist but none names a company the directory knows — which
             is exactly what the CS Toolbox import produces for a name it could
             not match. Say that, rather than showing an empty dropdown. */
          counts.client > 0 && (
            <p className="muted small mb-4">
              None of these {counts.client} client audits has a company set, so there is nothing to
              filter by yet. Open one and pick its client — the list comes from the shared company
              directory, so the company has to exist there first.
            </p>
          )
        ) : (
          <div className="row wrap mb-4">
            <select
              className="select"
              style={{ maxWidth: 320 }}
              value={clientId ?? ''}
              onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">All clients ({clientsWithAudits.length})</option>
              {clientsWithAudits.map((c) => (
                <option key={c.ClientId} value={c.ClientId}>
                  {c.Name}
                </option>
              ))}
            </select>
            {clientId !== null && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setClientId(null)}>
                Clear
              </button>
            )}
          </div>
        ))}

      {tab === 'catalog' ? (
        <Catalog
          onStart={(seed) => {
            setEditing({ audit: null, seed });
            window.scrollTo({ top: 0 });
          }}
        />
      ) : (
        <>
          {audits.loading && <Loading label="Loading audits" />}
          {!audits.loading &&
            (visible.length === 0 ? (
              <Empty
                title={
                  tab === 'client' && clientId !== null
                    ? 'No audits for that client yet'
                    : tab === 'all'
                      ? 'No audits yet'
                      : `No ${tab} audits`
                }
              >
                <p className="muted small">Start one from the Catalog tab, or create it from scratch.</p>
              </Empty>
            ) : (
              <div className="cs-audit-grid">
                {visible.map((audit) => (
                  <AuditCard
                    key={audit.CsAuditId}
                    audit={audit}
                    onEdit={() => setEditing({ audit })}
                    onChanged={refresh}
                    onError={setError}
                  />
                ))}
              </div>
            ))}
        </>
      )}

      {editing && (
        <AuditEditor
          audit={editing.audit}
          seed={editing.seed}
          clients={clients.data ?? []}
          employees={employees.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

/**
 * One audit, kept deliberately small.
 *
 * The checklist rolls up into the card rather than sitting open: sixteen
 * audits with their checklists expanded is a page nobody scrolls to the bottom
 * of, and most of these have no checklist at all. The count in the header is
 * the summary, and clicking it opens the list in place — ticking an item is
 * still one click once it is open.
 */
function AuditCard({
  audit,
  onEdit,
  onChanged,
  onError,
}: {
  audit: CsAudit;
  onEdit: () => void;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const done = audit.Items.filter((i) => i.IsDone).length;

  const toggle = (itemId: number, next: boolean) => {
    api
      .put(`/cs/audits/${audit.CsAuditId}/items/${itemId}`, { done: next })
      .then(onChanged)
      .catch((e: Error) => onError(e.message));
  };

  const setFrequency = (frequency: CsAuditFrequency) => {
    api
      .put(`/cs/audits/${audit.CsAuditId}/frequency`, { frequency })
      .then(onChanged)
      .catch((e: Error) => onError(e.message));
  };

  /* Dates are gone from this module, so the corner of the card that carried
     "Overdue" now carries the only status left that means anything: whether
     the checklist still has something outstanding. Cards with no checklist
     say nothing rather than claiming to be finished. */
  const outstanding = audit.Items.length - done;
  const when =
    audit.Items.length === 0 ? null : outstanding > 0 ? (
      <span className="cs-due">{outstanding} open</span>
    ) : (
      <span className="cs-never">Complete</span>
    );

  return (
    <section className="card cs-audit">
      <div className="cs-audit-head">
        <span className="grow">
          <span className="cs-audit-title">{audit.Title}</span>
          <span className="cs-audit-meta">
            {audit.AuditType === 'client' ? (audit.ClientName ?? 'Client') : 'Internal'}
            {audit.OwnerName && ` · ${audit.OwnerName}`}
          </span>
        </span>
        {when}
      </div>

      <div className="cs-audit-row">
        <div className="cs-freq" role="group" aria-label="How often this audit recurs">
          {FREQUENCY_ORDER.map((frequency) => (
            <button
              key={frequency}
              type="button"
              className={`cs-freq-btn${audit.Frequency === frequency ? ' on' : ''}`}
              aria-pressed={audit.Frequency === frequency}
              title={FREQUENCY_LABELS[frequency].long}
              onClick={() => setFrequency(frequency)}
            >
              {FREQUENCY_LABELS[frequency].short}
            </button>
          ))}
        </div>

        {audit.Items.length > 0 && (
          <button type="button" className="cs-roll" aria-expanded={open} onClick={() => setOpen(!open)}>
            <span className="mono">
              {done}/{audit.Items.length}
            </span>
            <span aria-hidden="true">{open ? '▴' : '▾'}</span>
          </button>
        )}

        <span className="cs-audit-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
            Edit
          </button>
          <DeleteButton
            what={audit.Title}
            onConfirm={() =>
              api
                .del(`/cs/audits/${audit.CsAuditId}`)
                .then(onChanged)
                .catch((e: Error) => onError(e.message))
            }
          />
        </span>
      </div>

      {open && (
        <ul className="cs-checklist cs-audit-list">
          {audit.Items.map((item) => (
            <li key={item.CsAuditItemId}>
              <label>
                <input
                  type="checkbox"
                  checked={item.IsDone}
                  onChange={(e) => toggle(item.CsAuditItemId, e.target.checked)}
                />
                <span className={item.IsDone ? 'cs-checked' : ''}>{item.ItemText}</span>
              </label>
            </li>
          ))}
          {audit.Notes.trim() && <li className="cs-audit-notes">{audit.Notes}</li>}
        </ul>
      )}
    </section>
  );
}

/** What the catalogue hands the editor to start from. */
interface SeedAudit {
  title: string;
  auditType: CsAuditType;
  notes: string;
  items: string[];
}

function AuditEditor({
  audit,
  seed,
  clients,
  employees,
  onClose,
  onSaved,
}: {
  audit: CsAudit | null;
  seed?: SeedAudit;
  clients: CsClient[];
  employees: Employee[];
  onClose: () => void;
  onSaved: () => void;
}) {
  /* A new audit is chosen from the catalogue rather than typed, so the picker
     needs the catalogue. An existing audit already has its title and does not
     — hence the null path, which skips the request entirely. */
  const catalog = useResource<CsCatalogAudit[]>(audit === null && !seed ? '/cs/catalog' : null);

  const [auditType, setAuditType] = useState<CsAuditType>(audit?.AuditType ?? seed?.auditType ?? 'internal');
  const [title, setTitle] = useState(audit?.Title ?? seed?.title ?? '');
  const [catalogId, setCatalogId] = useState<number | null>(null);
  const [catalogTier, setCatalogTier] = useState<CsTierKey>('foundational');
  const [clientId, setClientId] = useState<number | null>(audit?.ClientId ?? null);
  const [frequency, setFrequency] = useState<CsAuditFrequency>(audit?.Frequency ?? 'annual');
  const [notes, setNotes] = useState(audit?.Notes ?? seed?.notes ?? '');
  const [ownerEmployeeId, setOwnerEmployeeId] = useState<number | null>(audit?.OwnerEmployeeId ?? null);
  const [items, setItems] = useState<{ text: string; done: boolean }[]>(
    audit
      ? audit.Items.map((i) => ({ text: i.ItemText, done: i.IsDone }))
      : (seed?.items ?? []).map((text) => ({ text, done: false })),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /* Only entries that apply to the type being created. Choosing "client" and
     being offered an internal-only audit is the confusion migration 042
     exists to remove. */
  const offered = (catalog.data ?? []).filter(
    (e) => e.Applicability === 'both' || e.Applicability === auditType,
  );
  const chosen = offered.find((e) => e.CsCatalogAuditId === catalogId) ?? null;

  /** Picking an entry or a tier rewrites the title and the checklist. */
  const applyCatalog = (entry: CsCatalogAudit | null, tier: CsTierKey) => {
    if (!entry) {
      setTitle('');
      setItems([]);
      return;
    }
    const content = entry.Tiers[tier];
    setTitle(`${entry.Title} — ${TIERS.find((t) => t.key === tier)?.label ?? ''}`);
    setNotes(content.summary);
    setItems(content.items.map((text) => ({ text, done: false })));
  };

  const save = () => {
    if (!title.trim()) {
      setError('Choose an audit from the catalog.');
      return;
    }
    setSaving(true);
    setError(null);

    const body = {
      auditType,
      title,
      clientId,
      /* Echoed back unchanged. The Scheduled field is gone, but nulling this
         would quietly discard the date on any audit that still has one. */
      auditDate: audit?.AuditDate ? audit.AuditDate.slice(0, 10) : null,
      frequency,
      notes,
      ownerEmployeeId,
      items: items.filter((i) => i.text.trim()),
    };
    const request = audit
      ? api.put<CsAudit>(`/cs/audits/${audit.CsAuditId}`, body)
      : api.post<CsAudit>('/cs/audits', body);
    request.then(onSaved).catch((e: Error) => {
      setError(e.message);
      setSaving(false);
    });
  };

  return (
    <Modal
      title={audit ? audit.Title : seed ? `Start: ${seed.title}` : 'New audit'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      {/* An existing audit keeps the title it was created with; there is no
          picker, because re-basing an audit onto a different catalogue entry
          would silently replace the checklist somebody has been ticking. */}
      {audit !== null || seed ? (
        <Field label="Audit">
          <p className="strong">{title}</p>
        </Field>
      ) : (
        <>
          <Field
            label="Audit"
            hint={
              catalog.loading
                ? 'Loading the catalog…'
                : offered.length === 0
                  ? `No catalog entries apply to ${auditType} audits yet — set one to "${auditType}" or "both" on the Catalog tab.`
                  : 'Picking one fills in the checklist for the tier you choose'
            }
          >
            <select
              className="select"
              value={catalogId ?? ''}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null;
                setCatalogId(id);
                applyCatalog(offered.find((x) => x.CsCatalogAuditId === id) ?? null, catalogTier);
              }}
            >
              <option value="">Choose from the catalog…</option>
              {offered.map((entry) => (
                <option key={entry.CsCatalogAuditId} value={entry.CsCatalogAuditId}>
                  {entry.Title}
                </option>
              ))}
            </select>
          </Field>

          {chosen && (
            <Field label="Tier">
              <div className="status-group">
                {TIERS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`status-btn${catalogTier === t.key ? ' on met' : ''}`}
                    aria-pressed={catalogTier === t.key}
                    onClick={() => {
                      setCatalogTier(t.key);
                      applyCatalog(chosen, t.key);
                    }}
                  >
                    {t.label}
                    {chosen.Tiers[t.key].items.length > 0 && (
                      <span className="xs muted"> {chosen.Tiers[t.key].items.length}</span>
                    )}
                  </button>
                ))}
              </div>
            </Field>
          )}
        </>
      )}

      <div className="grid grid-2">
        <Field label="Type">
          <select
            className="select"
            value={auditType}
            onChange={(e) => {
              const next = e.target.value as CsAuditType;
              setAuditType(next);
              /* Switching type can make the chosen entry inapplicable. Clear
                 it rather than leaving a title from an entry the picker no
                 longer offers. */
              if (chosen && chosen.Applicability !== 'both' && chosen.Applicability !== next) {
                setCatalogId(null);
                applyCatalog(null, catalogTier);
              }
            }}
          >
            <option value="internal">Internal</option>
            <option value="client">Client</option>
          </select>
        </Field>

        {auditType === 'client' && (
          <Field label="Client" hint="From the shared company directory">
            <select
              className="select"
              value={clientId ?? ''}
              onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Not set</option>
              {clients.map((c) => (
                <option key={c.ClientId} value={c.ClientId}>
                  {c.Name}
                  {c.IsActive ? '' : ' (inactive)'}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="How often" hint="The cadence it recurs on">
          <select
            className="select"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as CsAuditFrequency)}
          >
            {FREQUENCY_ORDER.map((f) => (
              <option key={f} value={f}>
                {FREQUENCY_LABELS[f].long}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Owner">
          <select
            className="select"
            value={ownerEmployeeId ?? ''}
            onChange={(e) => setOwnerEmployeeId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Unassigned</option>
            {employees.map((e) => (
              <option key={e.EmployeeId} value={e.EmployeeId}>
                {e.FullName}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="field">
        <label>Checklist</label>
        {items.map((item, index) => (
          <div key={index} className="row">
            <input
              type="checkbox"
              checked={item.done}
              onChange={(e) =>
                setItems((current) =>
                  current.map((it, i) => (i === index ? { ...it, done: e.target.checked } : it)),
                )
              }
            />
            <input
              className="input grow"
              value={item.text}
              onChange={(e) =>
                setItems((current) =>
                  current.map((it, i) => (i === index ? { ...it, text: e.target.value } : it)),
                )
              }
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setItems((current) => current.filter((_, i) => i !== index))}
              aria-label="Remove this item"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-ghost btn-sm mt-2"
          onClick={() => setItems((current) => [...current, { text: '', done: false }])}
        >
          Add an item
        </button>
      </div>

      <Field label="Notes">
        <textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Modal>
  );
}

/**
 * The catalogue.
 *
 * Read from the API rather than a bundled constant, so entries can be added
 * and corrected here. The tier is chosen with a segmented control rather than
 * colour-coded chips: tier is an ambition level, and the palette's colour
 * scale means severity.
 */
function Catalog({ onStart }: { onStart: (seed: SeedAudit) => void }) {
  const catalog = useResource<CsCatalogAudit[]>('/cs/catalog');
  const [open, setOpen] = useState<number | null>(null);
  const [tier, setTier] = useState<CsTierKey>('foundational');
  const [editing, setEditing] = useState<{ entry: CsCatalogAudit | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small">
          Standard audits, each at three maturity tiers. Pick a tier to see its scope, then start it
          as a tracked audit — the checklist comes with it.
        </p>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({ entry: null })}>
          Add to catalog
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
      {catalog.error && <ErrorBanner message={catalog.error} />}
      {catalog.loading && <Loading label="Loading the catalog" />}

      {!catalog.loading && catalog.data?.length === 0 && (
        <Empty title="The catalog is empty">
          <p className="muted small">Add the first entry, or redeploy to re-seed the standard ten.</p>
        </Empty>
      )}

      <div className="grid grid-2">
        {(catalog.data ?? []).map((entry) => (
          <CatalogCard
            key={entry.CsCatalogAuditId}
            entry={entry}
            open={open === entry.CsCatalogAuditId}
            tier={tier}
            onOpen={() => setOpen(open === entry.CsCatalogAuditId ? null : entry.CsCatalogAuditId)}
            onTier={setTier}
            onStart={onStart}
            onEdit={() => setEditing({ entry })}
            onDelete={() =>
              api
                .del(`/cs/catalog/${entry.CsCatalogAuditId}`)
                .then(catalog.reload)
                .catch((e: Error) => setError(e.message))
            }
          />
        ))}
      </div>

      {editing && (
        <CatalogEditor
          entry={editing.entry}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            catalog.reload();
          }}
        />
      )}
    </>
  );
}

function CatalogCard({
  entry,
  open,
  tier,
  onOpen,
  onTier,
  onStart,
  onEdit,
  onDelete,
}: {
  entry: CsCatalogAudit;
  open: boolean;
  tier: CsTierKey;
  onOpen: () => void;
  onTier: (tier: CsTierKey) => void;
  onStart: (seed: SeedAudit) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const selected = entry.Tiers[tier];

  return (
    <section className="card cs-catalog">
      <button type="button" className="cs-catalog-head" onClick={onOpen} aria-expanded={open}>
        <span className="grow">
          <span className="strong">{entry.Title}</span>
          <div className="xs subtle">{entry.Blurb}</div>
        </span>
        {/* Only worth saying when it narrows something — "Both" is the default
            and labelling every card with it is noise. */}
        {entry.Applicability !== 'both' && (
          <span className="badge badge-neutral">
            {entry.Applicability === 'client' ? 'Client' : 'Internal'}
          </span>
        )}
        <span className="xs muted">{open ? 'Hide' : 'Show tiers'}</span>
      </button>

      {open && (
        <div className="card-body">
          <div className="status-group mb-3">
            {TIERS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`status-btn${tier === t.key ? ' on met' : ''}`}
                aria-pressed={tier === t.key}
                onClick={() => onTier(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {selected.summary ? (
            <p className="small">{selected.summary}</p>
          ) : (
            <p className="xs subtle">This tier has not been written yet.</p>
          )}

          {selected.items.length > 0 && (
            <ul className="cs-checklist mt-3">
              {selected.items.map((item, index) => (
                <li key={index}>
                  <span className="cs-catalog-item">{item}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="row-between wrap mt-4">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={selected.items.length === 0}
              onClick={() =>
                onStart({
                  /* The tier is in the title because a "Firewall" audit at
                     Foundational and one at Advanced are different pieces of
                     work, and the list only shows the title. */
                  title: `${entry.Title} — ${TIERS.find((t) => t.key === tier)?.label ?? ''}`,
                  auditType: 'internal',
                  notes: selected.summary,
                  items: selected.items,
                })
              }
            >
              Start this audit
            </button>
            <span className="row">
              <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
                Edit
              </button>
              <DeleteButton what={entry.Title} onConfirm={onDelete} />
            </span>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * Add or correct a catalogue entry.
 *
 * Each tier's checklist is a textarea, one item per line. A dozen rows of
 * inputs would be a worse way to write fifteen items than a block of text is,
 * and this is writing rather than ticking — the done state does not exist here.
 */
function CatalogEditor({
  entry,
  onClose,
  onSaved,
}: {
  entry: CsCatalogAudit | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(entry?.Title ?? '');
  const [blurb, setBlurb] = useState(entry?.Blurb ?? '');
  const [applicability, setApplicability] = useState<CsApplicability>(entry?.Applicability ?? 'both');
  const [tiers, setTiers] = useState<Record<CsTierKey, { summary: string; text: string }>>(() =>
    Object.fromEntries(
      TIERS.map((t) => [
        t.key,
        {
          summary: entry?.Tiers[t.key]?.summary ?? '',
          text: (entry?.Tiers[t.key]?.items ?? []).join('\n'),
        },
      ]),
    ) as Record<CsTierKey, { summary: string; text: string }>,
  );
  const [active, setActive] = useState<CsTierKey>('foundational');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const setTier = (key: CsTierKey, patch: Partial<{ summary: string; text: string }>) =>
    setTiers((current) => ({ ...current, [key]: { ...current[key], ...patch } }));

  const save = () => {
    if (!title.trim()) {
      setError('A catalog entry needs a title.');
      return;
    }
    setSaving(true);
    setError(null);

    const body = {
      title,
      blurb,
      applicability,
      tiers: Object.fromEntries(
        TIERS.map((t) => [
          t.key,
          {
            summary: tiers[t.key].summary,
            items: tiers[t.key].text
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean),
          },
        ]),
      ),
    };

    const request = entry
      ? api.put(`/cs/catalog/${entry.CsCatalogAuditId}`, body)
      : api.post('/cs/catalog', body);
    request.then(onSaved).catch((e: Error) => {
      setError(e.message);
      setSaving(false);
    });
  };

  return (
    <Modal
      title={entry ? entry.Title : 'Add to the catalog'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Title">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </Field>

      <Field label="What it covers" hint="One line, shown under the title">
        <input className="input" value={blurb} onChange={(e) => setBlurb(e.target.value)} />
      </Field>

      <Field label="Available for" hint="Which kind of audit can be started from this entry">
        <div className="status-group">
          {APPLICABILITY.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`status-btn${applicability === a.key ? ' on met' : ''}`}
              aria-pressed={applicability === a.key}
              onClick={() => setApplicability(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>
      </Field>

      <div className="tabs mb-4">
        {TIERS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`tab${active === t.key ? ' active' : ''}`}
            onClick={() => setActive(t.key)}
          >
            {t.label}
            {tiers[t.key].text.trim() && (
              <span className="xs muted">
                {' '}
                {tiers[t.key].text.split('\n').filter((l) => l.trim()).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <Field label={`${TIERS.find((t) => t.key === active)?.label} — scope`}>
        <textarea
          className="input"
          rows={2}
          value={tiers[active].summary}
          onChange={(e) => setTier(active, { summary: e.target.value })}
          placeholder="What this tier sets out to establish."
        />
      </Field>

      <Field label="Checklist" hint="One item per line">
        <textarea
          className="input"
          rows={12}
          value={tiers[active].text}
          onChange={(e) => setTier(active, { text: e.target.value })}
        />
      </Field>

      {entry?.CatalogKey && (
        <p className="xs subtle">
          This is one of the entries that ships with the app. Your edits stick — the seed only ever
          inserts what is missing. Deleting it, though, brings it back on the next deploy; rename it
          instead if you want it gone.
        </p>
      )}
    </Modal>
  );
}
